import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowUpRight, Menu, ShoppingBag, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Brand } from '@components/common/Brand';
import { ButtonLink } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { useCart } from '@context/CartContext';
import { navigationLinks } from '@config/navigation';
import { LanguageSelector } from '../navigation/LanguageSelector';
export const Header = () => {
    const { t } = useTranslation();
    const { totalItems, openCart } = useCart();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const firstMobileLinkRef = useRef(null);
    const menuButtonRef = useRef(null);
    const location = useLocation();
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);
    useEffect(() => {
        if (isMenuOpen)
            firstMobileLinkRef.current?.focus();
    }, [isMenuOpen]);
    useEffect(() => {
        const desktop = window.matchMedia('(min-width: 1200px)');
        const closeOnDesktop = () => {
            if (desktop.matches)
                setIsMenuOpen(false);
        };
        desktop.addEventListener('change', closeOnDesktop);
        return () => desktop.removeEventListener('change', closeOnDesktop);
    }, []);
    const closeMenu = () => {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
    };
    return (_jsxs(HeaderShell, { onKeyDown: (event) => {
            if (event.key === 'Escape' && isMenuOpen)
                closeMenu();
        }, children: [_jsx(SkipLink, { href: "#main-content", children: t('navigation.skipContent') }), _jsxs(HeaderInner, { children: [_jsx(BrandLink, { to: "/", "aria-label": `AITechCare — ${t('navigation.home')}`, onClick: () => setIsMenuOpen(false), children: _jsx(Brand, {}) }), _jsx(DesktopNav, { "aria-label": t('footer.navigation'), children: navigationLinks
                            .filter((link) => link.to !== '/contact')
                            .map((link) => (_jsx(StyledNavLink, { to: link.to, end: link.to === '/', children: t(link.labelKey) }, link.to))) }), _jsxs(Actions, { children: [_jsxs(DesktopCta, { to: "/contact", children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 16, "aria-hidden": "true" })] }), _jsx(LanguageSelector, {}), _jsxs(CartButton, { type: "button", "aria-label": `${t('navigation.cart')} (${totalItems})`, onClick: () => {
                                    setIsMenuOpen(false);
                                    openCart();
                                }, children: [_jsx(ShoppingBag, { size: 20, "aria-hidden": "true" }), totalItems > 0 && _jsx(CartCount, { "aria-hidden": "true", children: totalItems })] }), _jsx(MenuButton, { ref: menuButtonRef, type: "button", "aria-label": isMenuOpen ? t('navigation.closeMenu') : t('navigation.openMenu'), "aria-expanded": isMenuOpen, "aria-controls": "mobile-navigation", onClick: () => setIsMenuOpen((current) => !current), children: isMenuOpen ? (_jsx(X, { size: 22, "aria-hidden": "true" })) : (_jsx(Menu, { size: 22, "aria-hidden": "true" })) })] })] }), _jsx(MobileNav, { id: "mobile-navigation", "$isOpen": isMenuOpen, "aria-label": t('footer.navigation'), children: _jsxs(Container, { children: [navigationLinks.map((link, index) => (_jsxs(StyledNavLink, { ref: index === 0 ? firstMobileLinkRef : undefined, to: link.to, end: link.to === '/', onClick: closeMenu, children: [t(link.labelKey), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] }, link.to))), _jsxs(ButtonLink, { to: "/contact", onClick: closeMenu, children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] }) })] }));
};
const HeaderShell = styled.header `
  position: sticky;
  top: 0;
  z-index: 20;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
`;
const HeaderInner = styled(Container) `
  display: flex;
  min-height: 88px;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  @media (max-width: 1199px) {
    min-height: 72px;
  }
  @media (max-width: 374px) {
    width: calc(100% - 1.5rem);
    gap: 0.25rem;
  }
`;
const BrandLink = styled(NavLink) `
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  @media (max-width: 374px) {
    > span {
      font-size: 1.1rem;
      gap: 0.3rem;
    }
    svg {
      width: 28px;
      height: 28px;
    }
  }
`;
const DesktopNav = styled.nav `
  display: none;
  align-items: center;
  gap: 2rem;
  @media (min-width: 1200px) {
    display: flex;
  }
`;
const StyledNavLink = styled(NavLink) `
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.875rem;
  font-weight: 400;
  text-decoration: underline;
  text-underline-offset: 7px;
  text-decoration-color: transparent;
  transition: text-decoration-color 180ms ease;
  &.active,
  &:hover {
    text-decoration-color: ${({ theme }) => theme.colors.blue600};
  }
`;
const Actions = styled.div `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: 767px) {
    gap: 0;
  }
`;
const DesktopCta = styled(ButtonLink) `
  display: none;
  font-size: 0.8125rem;
  margin-right: 1rem;
  @media (min-width: 1200px) {
    display: inline-flex;
  }
`;
const CartButton = styled.button `
  position: relative;
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.navy900};
  background: transparent;
  &:hover {
    background: ${({ theme }) => theme.colors.mint50};
  }
`;
const CartCount = styled.span `
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  padding-inline: 3px;
  border-radius: 9px;
  color: white;
  background: ${({ theme }) => theme.colors.blue600};
  font-size: 0.625rem;
  font-weight: 700;
`;
const MenuButton = styled(CartButton) `
  @media (min-width: 1200px) {
    display: none;
  }
`;
const MobileNav = styled.nav `
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
  max-height: calc(100dvh - 72px);
  overflow-y: auto;
  > div {
    display: grid;
    padding-block: 1rem 1.5rem;
  }
  ${StyledNavLink} {
    justify-content: space-between;
    min-height: 54px;
    font-size: 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  }
  ${ButtonLink} {
    margin-top: 1.25rem;
  }
  @media (min-width: 1200px) {
    display: none;
  }
`;
const SkipLink = styled.a `
  position: fixed;
  left: 1rem;
  top: -10rem;
  z-index: 100;
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.navy900};
  &:focus {
    top: 1rem;
  }
`;
